/**
 * NETWORK STATUS MANAGER
 *
 * Detects online/offline status and provides hooks for React components.
 *
 * URL resolution: uses the central ENV.API_BASE_URL so the connectivity
 * ping hits the SAME backend axios + the rest of the app talk to. Earlier
 * versions had a hand-rolled `import.meta.env.VITE_API_URL || 'localhost:8080'`
 * fallback, which (a) ignored the empty-string-means-Vite-proxy convention
 * baked into env.ts and (b) defaulted to the wrong port (backend runs on
 * 8000 in dev, per backend/.env). Result: every 30s the ping landed on
 * a dead port, OfflineBanner flagged "connection lost", and the user
 * thought the backend had died — even though the real backend was fine.
 */

import { ENV } from '../config/env';

type NetworkStatusListener = (isOnline: boolean) => void;

class NetworkStatusManager {
  private listeners: Set<NetworkStatusListener> = new Set();
  private _isOnline: boolean = navigator.onLine;

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
      
      // Also check via fetch (more reliable)
      this.startConnectivityChecks();
    }
  }

  private handleOnline = () => {
    console.log('[Network] 🌐 Connection restored');
    this.setOnlineStatus(true);
  };

  private handleOffline = () => {
    console.log('[Network] 📴 Connection lost');
    this.setOnlineStatus(false);
  };

  private setOnlineStatus(isOnline: boolean) {
    if (this._isOnline !== isOnline) {
      this._isOnline = isOnline;
      this.notifyListeners();
    }
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this._isOnline));
  }

  /**
   * Periodically check connectivity via ping
   */
  private startConnectivityChecks() {
    setInterval(() => {
      this.checkConnectivity();
    }, 30000); // Check every 30 seconds
  }

  /**
   * Check connectivity by pinging a reliable endpoint.
   *
   * URL resolution mirrors apiClient: when ENV.API_BASE_URL is empty
   * (dev with Vite proxy), the fetch goes to a relative `/health` and
   * the Vite proxy forwards it to the backend port. When non-empty
   * (production / explicit override) we hit the absolute URL.
   */
  private async checkConnectivity(): Promise<boolean> {
    try {
      const healthUrl = ENV.API_BASE_URL ? `${ENV.API_BASE_URL}/health` : '/health';
      await fetch(healthUrl, {
        method: 'HEAD',
        cache: 'no-cache',
      });

      const isOnline = true; // If fetch succeeds, we're online
      this.setOnlineStatus(isOnline);
      return isOnline;
    } catch {
      const isOnline = false;
      this.setOnlineStatus(isOnline);
      return isOnline;
    }
  }

  /**
   * Get current online status
   */
  get isOnline(): boolean {
    return this._isOnline;
  }

  /**
   * Subscribe to network status changes
   */
  subscribe(listener: NetworkStatusListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Manually trigger connectivity check
   */
  async check(): Promise<boolean> {
    return this.checkConnectivity();
  }

  /**
   * Cleanup
   */
  destroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
    this.listeners.clear();
  }
}

// Export singleton
export const networkStatus = new NetworkStatusManager();
